
import React from "react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { FileText, ArrowLeft } from "lucide-react";

export default function TermsOfUse() {
  return (
    <div className="container mx-auto px-4 py-12 max-w-4xl" id="terms-top">
      <div className="mb-8">
        <Button variant="ghost" asChild className="mb-4">
          <Link to="/" className="flex items-center">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Home
          </Link>
        </Button>
        
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <FileText className="h-6 w-6" />
          Terms of Use
        </h1>
        <p className="text-muted-foreground mt-2">Last updated: April 17, 2025</p>
      </div>
      
      <div className="prose dark:prose-invert max-w-none">
        <p>
          Welcome to Ogmai. These Terms of Use ("Terms") govern your access to and use of Ogmai's proprietary platform, websites, services, and any other software or digital solutions we may provide (collectively, the "Ogmai Platform" or "Services").
        </p>
        
        <p className="font-bold">
          PLEASE READ THESE TERMS CAREFULLY BEFORE USING THE PLATFORM. BY CLICKING "ACCEPT" OR OTHERWISE ACCESSING OR USING THE PLATFORM, YOU ACKNOWLEDGE THAT YOU HAVE READ, UNDERSTOOD, AND AGREE TO BE LEGALLY BOUND BY THESE TERMS. IF YOU DO NOT ACCEPT THESE TERMS, YOU MAY NOT ACCESS OR USE THE OGMAI PLATFORM.
        </p>
        
        <hr className="my-8" />
        
        <section className="mb-8">
          <h2 className="text-2xl font-bold">1. ACCESS AND USE</h2>
          
          <h3 className="text-xl font-semibold mt-6">1.1 License to Use</h3>
          <p>
            Subject to these Terms, Ogmai grants you and your Authorized Users a limited, non-exclusive, non-transferable, and revocable license to access and use the Ogmai Platform solely for your internal business purposes.
          </p>
          
          <h3 className="text-xl font-semibold mt-6">1.2 Eligibility</h3>
          <p>
            By registering for an Ogmai account or using our Services, you represent and warrant that you are at least 18 years of age or the legal age of majority in your jurisdiction, and that you have the right, authority, and capacity to enter into and abide by these Terms.
          </p>
          
          <h3 className="text-xl font-semibold mt-6">1.3 User Responsibilities</h3>
          <p>
            You are responsible for all activities that occur under your account. You agree to:
          </p>
          <ul>
            <li>Keep your login credentials secure and confidential.</li>
            <li>Notify Ogmai immediately of any unauthorized use or suspected breach.</li>
            <li>Ensure that your use of the Platform does not violate any applicable law or regulation.</li>
          </ul>
          
          <h3 className="text-xl font-semibold mt-6">1.4 Prohibited Uses</h3>
          <p>
            You may not, and will not permit any third party to:
          </p>
          <ul>
            <li>Reverse engineer, disassemble, or decompile any part of the Platform.</li>
            <li>Use the Platform to develop a competing product or service.</li>
            <li>Sell, sublicense, lease, or otherwise exploit any part of the Platform commercially.</li>
            <li>Interfere with or disrupt the security, integrity or performance of the Platform.</li>
            <li>Upload, transmit, or distribute viruses or malicious code.</li>
          </ul>
        </section>
        
        <hr className="my-8" />
        
        <section className="mb-8">
          <h2 className="text-2xl font-bold">2. CUSTOMER DATA & FEEDBACK</h2>
          
          <h3 className="text-xl font-semibold mt-6">2.1 Customer Data</h3>
          <p>
            You retain all ownership rights to the data you upload or generate through use of the Platform ("Customer Data"). You grant Ogmai a worldwide, non-exclusive, royalty-free license to use, copy, process, store, transmit, and display your Customer Data solely to provide and improve our Services.
          </p>
          
          <h3 className="text-xl font-semibold mt-6">2.2 Feedback</h3>
          <p>
            Any suggestions, feedback, or ideas submitted by you become the property of Ogmai. You grant Ogmai a perpetual, irrevocable, sublicensable license to use such feedback without restriction or compensation.
          </p>
        </section>
        
        <hr className="my-8" />
        
        <section className="mb-8">
          <h2 className="text-2xl font-bold">3. PAYMENT</h2>
          
          <h3 className="text-xl font-semibold mt-6">3.1 Fees</h3>
          <p>
            Your access to the Ogmai Platform may be subject to payment of applicable fees. All fees are specified in your subscription or custom Order Form.
          </p>
          
          <h3 className="text-xl font-semibold mt-6">3.2 Payment Terms</h3>
          <p>
            Unless otherwise agreed, payments are due in advance and are non-refundable. Late payments may incur a charge of 1.5% monthly or the highest amount permitted by law.
          </p>
          
          <h3 className="text-xl font-semibold mt-6">3.3 Taxes</h3>
          <p>
            Fees are exclusive of any taxes or duties. You are responsible for all taxes except those based on Ogmai's income.
          </p>
        </section>
        
        <hr className="my-8" />
        
        <section className="mb-8">
          <h2 className="text-2xl font-bold">4. INTELLECTUAL PROPERTY</h2>
          <p>
            Ogmai and its licensors retain all rights, title, and interest in the Platform, including all technology, content, and proprietary information. Nothing in these Terms transfers ownership to you.
          </p>
          <p>
            You agree not to use the Ogmai brand, trademarks, or logos without prior written consent.
          </p>
        </section>
        
        <hr className="my-8" />
        
        <section className="mb-8">
          <h2 className="text-2xl font-bold">5. CONFIDENTIALITY</h2>
          
          <h3 className="text-xl font-semibold mt-6">5.1 Definition</h3>
          <p>
            "Confidential Information" includes all information, business plans, software, designs, technical data, and other materials disclosed by either party that are labeled confidential or should reasonably be considered confidential.
          </p>
          
          <h3 className="text-xl font-semibold mt-6">5.2 Non-Disclosure</h3>
          <p>
            Each party agrees to:
          </p>
          <ul>
            <li>Keep Confidential Information private.</li>
            <li>Only use it to fulfill obligations under these Terms.</li>
            <li>Not disclose it to third parties without written consent.</li>
          </ul>
        </section>
        
        <hr className="my-8" />
        
        <section className="mb-8">
          <h2 className="text-2xl font-bold">6. WARRANTIES AND DISCLAIMERS</h2>
          
          <h3 className="text-xl font-semibold mt-6">6.1 No Warranty</h3>
          <p>
            The Ogmai Platform is provided "as is." We disclaim all warranties, whether express, implied or statutory, including merchantability, fitness for a particular purpose, and non-infringement.
          </p>
          
          <h3 className="text-xl font-semibold mt-6">6.2 AI Content</h3>
          <p>
            Ogmai uses artificial intelligence to support certain features. While we aim for accuracy, we do not guarantee the accuracy or appropriateness of AI-generated outputs. You are responsible for verifying AI responses before relying on them.
          </p>
        </section>
        
        <hr className="my-8" />
        
        <section className="mb-8">
          <h2 className="text-2xl font-bold">7. LIMITATION OF LIABILITY</h2>
          <p>
            To the maximum extent permitted by law:
          </p>
          <ul>
            <li>Ogmai will not be liable for indirect, incidental, special, consequential, or exemplary damages.</li>
            <li>Our total liability for claims under these Terms will not exceed the amount paid by you in the 12 months preceding the event.</li>
          </ul>
        </section>
        
        <hr className="my-8" />
        
        <section className="mb-8">
          <h2 className="text-2xl font-bold">8. TERMINATION</h2>
          
          <h3 className="text-xl font-semibold mt-6">8.1 Termination by Either Party</h3>
          <p>
            You may terminate your account at any time. We may suspend or terminate your account immediately if you violate these Terms or misuse the Platform.
          </p>
          
          <h3 className="text-xl font-semibold mt-6">8.2 Consequences of Termination</h3>
          <p>
            Upon termination:
          </p>
          <ul>
            <li>Your access to the Platform will end.</li>
            <li>You must cease all use of the Platform.</li>
            <li>Any outstanding payments become immediately due.</li>
          </ul>
        </section>
        
        <hr className="my-8" />
        
        <section className="mb-8">
          <h2 className="text-2xl font-bold">9. GOVERNING LAW AND DISPUTES</h2>
          <p>
            These Terms are governed by the laws of the State of New York, without regard to conflict of laws principles. Any disputes will be resolved exclusively in courts located in New York, NY.
          </p>
        </section>
        
        <hr className="my-8" />
        
        <section className="mb-8">
          <h2 className="text-2xl font-bold">10. GENERAL PROVISIONS</h2>
          <ul>
            <li><strong>Entire Agreement:</strong> These Terms constitute the entire agreement between you and Ogmai.</li>
            <li><strong>Amendments:</strong> We may update these Terms. Continued use after updates signifies acceptance.</li>
            <li><strong>Assignment:</strong> You may not assign your rights without our written consent.</li>
            <li><strong>Waiver:</strong> Our failure to enforce any part of these Terms is not a waiver.</li>
            <li><strong>Force Majeure:</strong> Neither party is liable for delays due to causes beyond their control.</li>
          </ul>
        </section>
        
        <p className="mt-8">
          For any questions, contact: <a href="mailto:legal@ogmai.com" className="text-blue-600 hover:underline">legal@ogmai.com</a>
        </p>
      </div>
    </div>
  );
}
